import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { BudgetRevision, RevisionReasonCategory } from '@/types/budget.types';
import {
  Clock,
  User,
  FileText,
  TrendingUp,
  TrendingDown,
  ArrowRightLeft,
  CalendarClock,
  Users,
  AlertTriangle,
  FileEdit,
  ShieldAlert,
  Building2,
  Lightbulb,
  Hammer,
} from 'lucide-react';

// ─── Category helpers (aligned with RevisionAuditCard) ───────────────────────

const CATEGORY_COLOURS: Record<RevisionReasonCategory | 'custom' | string, string> = {
  'scope-change':          'bg-accent-nile-50 text-accent-nile-900 border-accent-nile-500',
  'client-request':        'bg-accent-purple-50 text-accent-purple-900 border-accent-purple-500',
  'resource-substitution': 'bg-accent-orange-50 text-accent-orange-900 border-accent-orange-500',
  'rate-change':           'bg-accent-lagoon-50 text-accent-lagoon-900 border-accent-lagoon-500',
  'risk-mitigation':       'bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500',
  'estimation-correction': 'bg-accent-destructive-50 text-accent-destructive-900 border-accent-destructive-500',
  'phase-extension':       'bg-accent-blue-violet-50 text-accent-blue-violet-900 border-accent-blue-violet-500',
  'efficiency-gain':       'bg-accent-success-50 text-accent-success-900 border-accent-success-500',
  custom:                  'bg-neutral-100 text-neutral-700 border-neutral-300',
};

const CATEGORY_ICONS: Record<RevisionReasonCategory | 'custom' | string, React.ElementType> = {
  'scope-change':          ArrowRightLeft,
  'client-request':        FileEdit,
  'resource-substitution': Users,
  'rate-change':           Hammer,
  'risk-mitigation':       ShieldAlert,
  'estimation-correction': AlertTriangle,
  'phase-extension':       CalendarClock,
  'efficiency-gain':       Building2,
  custom:                  Lightbulb,
};

// ─── Status dot colour ────────────────────────────────────────────────────────

function getStatusDotColour(status: string): string {
  switch (status) {
    case 'current':
    case 'active':   return 'bg-accent-success-500';
    case 'draft':    return 'bg-accent-warning-500';
    case 'baseline': return 'bg-accent-nile-500';
    default:         return 'bg-neutral-300';
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'current':
    case 'active':
      return <Badge variant="outline" className="text-xs bg-accent-success-50 text-accent-success-900 border-accent-success-500">Current</Badge>;
    case 'draft':
      return <Badge variant="outline" className="text-xs bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500">Draft</Badge>;
    case 'baseline':
      return <Badge variant="outline" className="text-xs bg-accent-nile-50 text-accent-nile-900 border-accent-nile-500">Historical</Badge>;
    case 'historical':
      return <Badge variant="outline" className="text-xs bg-neutral-100 text-neutral-600 border-neutral-300">Historical</Badge>;
    default:
      return null;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDaysSince(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

// ─── Component ────────────────────────────────────────────────────────────────

interface BudgetRevisionTimelineProps {
  revisions: BudgetRevision[];
}

export function BudgetRevisionTimeline({ revisions }: BudgetRevisionTimelineProps) {
  if (revisions.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No revisions to display
      </div>
    );
  }

  return (
    <div className="space-y-0 py-4">
      <div className="relative">
        {/* Vertical timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

        <div className="space-y-6">
          {revisions.map((revision, index) => {
            const isCurrent = revision.status === 'current' || revision.status === 'active';
            const isDraft = revision.status === 'draft';
            const date = new Date(revision.createdAt ?? revision.date).toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
            });
            const author = revision.createdBy ?? revision.changedByName;
            const hoursDelta = revision.hoursChange ?? 0;
            const colourClass = CATEGORY_COLOURS[revision.reasonCategory ?? 'custom'] ?? CATEGORY_COLOURS.custom;
            const CatIcon = CATEGORY_ICONS[revision.reasonCategory ?? 'custom'] ?? FileText;
            const categoryLabel = revision.reasonCategoryLabel
              ?? revision.reasonCategory?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

            return (
              <div key={revision.revisionId ?? revision.id} className="relative pl-14">
                {/* Timeline dot */}
                <div className={`absolute left-4 top-3 h-5 w-5 rounded-full border-4 border-background ${getStatusDotColour(revision.status)}`} />

                <Card className={`${isCurrent ? 'border-2 border-primary bg-primary/5' : ''} ${isDraft ? 'border-dashed border-accent-warning-500 bg-accent-warning-50/30' : ''}`}>
                  <CardContent className="pt-4 pb-4 space-y-3">

                    {/* ── Header row ── */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-base text-foreground">
                          {revision.revisionNumber}
                        </span>
                        {getStatusBadge(revision.status)}
                        {revision.reasonCategory && (
                          <Badge variant="outline" className={`gap-1 text-xs font-medium ${colourClass}`}>
                            <CatIcon className="h-3 w-3" />
                            {categoryLabel}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {date}
                        </span>
                        {author && (
                          <span className="flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            {author}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* ── Reason ── */}
                    {revision.reason && (
                      <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                        {revision.reason}
                      </p>
                    )}

                    {/* ── Hours delta + cascade ── */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {hoursDelta !== 0 && (
                        <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ${
                          hoursDelta > 0
                            ? 'bg-accent-warning-50 text-accent-warning-900'
                            : 'bg-accent-success-50 text-accent-success-900'
                        }`}>
                          {hoursDelta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {hoursDelta > 0 ? '+' : ''}{hoursDelta.toLocaleString()}h
                        </span>
                      )}
                      {revision.cascadeRecord?.costBudgetUpdated && (
                        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-accent-nile-50 text-accent-nile-900 font-medium">
                          Cost cascaded
                        </span>
                      )}
                      {revision.cascadeRecord?.salesBudgetUpdated && (
                        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-accent-purple-50 text-accent-purple-900 font-medium">
                          Sales cascaded
                        </span>
                      )}
                    </div>

                    {/* ── Days since previous ── */}
                    {index < revisions.length - 1 && (
                      <div className="text-xs text-muted-foreground border-t pt-2">
                        {getDaysSince(
                          revisions[index + 1].createdAt ?? revisions[index + 1].date,
                          revision.createdAt ?? revision.date
                        )} days since previous revision
                      </div>
                    )}

                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Summary footer ── */}
      <div className="pt-6 pl-14">
        <div className="flex items-center gap-6 text-sm text-muted-foreground border-t pt-4">
          <span><strong className="text-foreground">{revisions.length}</strong> total revisions</span>
          {revisions.length > 1 && (
            <span>
              <strong className="text-foreground">
                {getDaysSince(
                  revisions[revisions.length - 1].createdAt ?? revisions[revisions.length - 1].date,
                  revisions[0].createdAt ?? revisions[0].date
                )}
              </strong> days of revision history
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
